import { useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useAppState } from "../../../context/StateContext";
import useSyncUser from "../../../hooks/useSyncUser";
import { User } from "../../../models/user";
import { Warehouse } from "../../../models/warehouse";
import UserService from "../../../services/user.service";
import WarehouseService from "../../../services/warehouse.service";
import { Status } from "../../../util/enums";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import { getAllWarehouseOption, getEmptyWarehouseOption } from "../util";

const defaultAttribute = ["id", "name"];

export const useWarehouseActions = (showAllOption = false) => {
  const {
    currentUser,
    setWarehouse,
    warehouseList,
    setWarehouseList,
    hasUserChanged,
    setHasUserChanged,
  } = useAppState();

  const allWarehouseOption = getAllWarehouseOption();
  const emptyWarehouseOption = getEmptyWarehouseOption();

  const { mutate: syncCurrentUser } = useSyncUser();

  const {
    isFetching: isFetchingWarehouseList,
    isSuccess: isSuccessWarehouseList,
    refetch: refetchWarehouseList,
  } = useQuery(
    ["warehouse.list", currentUser, hasUserChanged],
    () =>
      WarehouseService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
        include: JSON.stringify([
          {
            association: "branches",
            where: {
              id: currentUser?.branches?.map((branch) => branch.id),
            },
          },
          {
            association: "sections",
            attributes: [...defaultAttribute, "allowsItemLoose", "allowsStorageBox", "isActive"],
            include: [
              {
                association: "maps",
                attributes: [...defaultAttribute, "description", "isActive"],
              },
              {
                association: "storageBoxTypes",
                attributes: defaultAttribute,
              },
            ],
          },
        ]),
      }),
    {
      enabled: hasUserChanged,
      refetchOnWindowFocus: false,
      onSuccess: (responseWarehouseList) => {
        let currentWarehouse = emptyWarehouseOption;

        if (responseWarehouseList?.data?.length) {
          const firstWarehouseInList = responseWarehouseList.data[0];

          setHasUserChanged(false);
          setWarehouseList(responseWarehouseList.data);

          if (currentUser?.warehouseId) {
            const warehouseInList = responseWarehouseList.data.find(
              (warehouse) => warehouse.id === currentUser.warehouseId
            );

            currentWarehouse = warehouseInList ?? firstWarehouseInList;
          } else {
            currentWarehouse = firstWarehouseInList;
          }
        } else {
          setWarehouseList([]);
        }

        setWarehouse(currentWarehouse);

        // ✅ só atualiza usuário se realmente mudou
        const nextWarehouseId =
          currentWarehouse?.id === emptyWarehouseOption.id ? null : currentWarehouse?.id;

        if (currentUser?.warehouseId !== nextWarehouseId) {
          mutateUpdateUserWithWarehouse({
            data: { warehouseId: nextWarehouseId } as Partial<User>,
          });
        }
      },
    }
  );

  const {
    mutate: mutateUpdateUserWithWarehouse,
    isLoading: isLoadingUpdateUser,
    isError: isErrorUpdateUser,
  } = useMutation(
    ["update.user.with.warehouse"],
    ({ data }: { data: Partial<User> }) => UserService.update(currentUser?.id, data as any),
    {
      onSuccess: () => {
        syncCurrentUser();
      },
      onError: (error: AxiosError<Error>) => {
        if (error?.response) {
          NotificationToast.error((error.response.data as any)?.message);
        }
        refetchWarehouseList();
      },
    }
  );

  const warehouseListMerged = useMemo(() => {
    if (warehouseList?.length) {
      if (showAllOption) return [allWarehouseOption, ...warehouseList] as Warehouse[];
      return warehouseList;
    }

    return [emptyWarehouseOption];
  }, [warehouseList, showAllOption]);

  const handleUpdateWarehouse = (warehouseId: number) => {
    const selectedWarehouse = warehouseListMerged?.find((item) => item.id === warehouseId);

    setWarehouse?.(selectedWarehouse);

    if (selectedWarehouse && selectedWarehouse.id !== allWarehouseOption.id) {
      const nextWarehouseId =
        selectedWarehouse.id === emptyWarehouseOption.id ? null : selectedWarehouse.id;

      return mutateUpdateUserWithWarehouse({
        data: { warehouseId: nextWarehouseId } as Partial<User>,
      });
    }
  };

  useEffect(() => {
    if (!showAllOption && currentUser?.warehouse) setWarehouse(currentUser.warehouse);
  }, [showAllOption]);

  return {
    isFetchingWarehouseList,
    isSuccessWarehouseList,
    handleUpdateWarehouse,
    isLoadingUpdateUser,
    isErrorUpdateUser,
    warehouseListMerged,
    setWarehouse,
  };
};
